/**
 * {@link DataTable} for establishments.
 */
import * as React from 'react';
import { Building2, HelpCircle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { EstablishmentRow } from './EstablishmentRow';
import { EstablishmentCard } from './EstablishmentCard';
import { EstablishmentFiltersContent } from './EstablishmentFilters';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DetailsViewContent } from '@/components/common/details/DetailsViewContent';
import { renderEstablishmentDetails } from './renderEstablishmentDetails';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import {
  getEstablishments,
  deleteEstablishment,
  exportEstablishmentsExcel,
} from '@/services/establishments.service';
import { cn } from '@/lib/utils';
import {
  hasEstablishmentActiveFilters,
  toEstablishmentListParams,
} from './establishment-table-utils';
import {
  DEFAULT_ESTABLISHMENT_FILTERS,
  ALL_FILTER_GROUP_IDS,
  FILTER_GROUP_LABELS,
  ESTABLISHMENT_SUIVI_COLUMNS,
  ESTABLISHMENT_OPENING_DELAY_COLUMN,
  type EstablishmentFilters,
  type FilterGroupId,
} from './establishment-configs';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import type {
  Establishment,
  EstablishmentCategory,
  EstablishmentPriorityNetwork,
  EstablishmentType,
  EstablishmentStatus,
  ProspectionStatus,
} from '@/types/establishment.types';

/** Props for {@link EstablishmentsDataTable}. */
interface EstablishmentsDataTableProps {
  establishments?: Establishment[];
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  layoutWidth?: ElementWidth;
  enableFilters?: boolean;
  defaultFilters?: EstablishmentFilters;
  showCreateButton?: boolean;
  onCreateClick?: () => void;
  onEditEstablishment?: (establishment: Establishment) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  isSection?: boolean;
  pageActions?: {
    canEditRow?: boolean;
    canDeleteRow?: boolean;
    canExportCsv?: boolean;
  };
}

/** Column header with a help tooltip (used for tracking / opening-delay columns). */
function EstablishmentTrackingHeader({
  label,
  tooltip,
}: Readonly<{ label: string; tooltip: string }>) {
  return (
    <span className="inline-flex items-center justify-center gap-1">
      {label}
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center text-muted-foreground hover:text-foreground"
              aria-label={tooltip}
            >
              <HelpCircle className="w-3.5 h-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-sm">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </span>
  );
}

/** Establishments list / grid with filters, details, delete, and Excel export. */
export const EstablishmentsDataTable: React.FC<EstablishmentsDataTableProps> = ({
  establishments: externalEstablishments,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  layoutWidth = 'full',
  enableFilters = true,
  defaultFilters = DEFAULT_ESTABLISHMENT_FILTERS,
  showCreateButton = false,
  onCreateClick,
  onEditEstablishment,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  isSection = false,
  pageActions,
}) => {
  const queryClient = useQueryClient();
  const showExport = pageActions?.canExportCsv === true;

  const dt = useDataTable<EstablishmentFilters, FilterGroupId>({
    tableName: 'establishments',
    defaultFilters,
    allFilterGroupIds: ALL_FILTER_GROUP_IDS,
    layoutWidth,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ['establishments', dt.currentPage, dt.itemsPerPage, dt.filters],
    queryFn: async () =>
      getEstablishments({
        page: dt.currentPage,
        pageSize: dt.itemsPerPage,
        ...toEstablishmentListParams(dt.filters),
      }),
    enabled: !externalEstablishments,
  });

  const establishments = React.useMemo(
    () => externalEstablishments ?? (apiResponse?.data || []),
    [externalEstablishments, apiResponse?.data],
  );
  const totalItems = apiResponse?.total || establishments.length;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  const [isExporting, setIsExporting] = React.useState(false);
  const isEmptyForExport = React.useMemo(
    () => (externalEstablishments ? establishments.length === 0 : !isLoading && totalItems === 0),
    [externalEstablishments, establishments.length, isLoading, totalItems],
  );

  /** Excel export of the current filter set (blocked when the list is empty). */
  const handleExportExcel = React.useCallback(async () => {
    if (isEmptyForExport) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await exportEstablishmentsExcel(toEstablishmentListParams(dt.filters));
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [dt.filters, isEmptyForExport]);

  const {
    data: expandedEstablishmentData,
    isLoading: isLoadingExpandedEstablishment,
    isError: isExpandedEstablishmentError,
    error: expandedEstablishmentError,
  } = useQuery({
    queryKey: ['establishments', expandedRowId],
    queryFn: async () => {
      if (!expandedRowId) return null;
      const { getEstablishment } = await import('@/services/establishments.service');
      return await getEstablishment(expandedRowId);
    },
    enabled: !!expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedEstablishmentError,
    error: expandedEstablishmentError,
    catalogKey: 'establishmentNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const hasActiveFilters = React.useMemo(
    () => hasEstablishmentActiveFilters(dt.filters),
    [dt.filters],
  );

  const deleteMutation = useMutation({
    mutationFn: deleteEstablishment,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['establishments'] });
      dt.setDeleteId(null);
      toast.success('Établissement supprimé avec succès !');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const establishmentToDelete = React.useMemo(
    () => establishments.find((e) => e.id === dt.deleteId),
    [establishments, dt.deleteId],
  );

  const toggleTypeFilter = React.useCallback(
    (type: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        types: prev.types.includes(type as EstablishmentType)
          ? prev.types.filter((t) => t !== type)
          : [...prev.types, type as EstablishmentType],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const toggleCategoryFilter = React.useCallback(
    (category: string) => {
      dt.setFilters((prev) => {
        const cats = prev.categories ?? [];
        return {
          ...prev,
          categories: cats.includes(category as EstablishmentCategory)
            ? cats.filter((c) => c !== category)
            : [...cats, category as EstablishmentCategory],
        };
      });
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const togglePriorityNetworkFilter = React.useCallback(
    (network: string) => {
      dt.setFilters((prev) => {
        const nets = prev.priorityNetworks ?? [];
        return {
          ...prev,
          priorityNetworks: nets.includes(network as EstablishmentPriorityNetwork)
            ? nets.filter((n) => n !== network)
            : [...nets, network as EstablishmentPriorityNetwork],
        };
      });
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const toggleStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        statuses: prev.statuses.includes(status as EstablishmentStatus)
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status as EstablishmentStatus],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const toggleProspectionStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        prospectionStatuses: prev.prospectionStatuses.includes(status as ProspectionStatus)
          ? prev.prospectionStatuses.filter((s) => s !== status)
          : [...prev.prospectionStatuses, status as ProspectionStatus],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setCity = React.useCallback(
    (city: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, cities: city ? [city] : [] }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setDepartment = React.useCallback(
    (dept: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, departments: dept ? [dept] : [] }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setCalendarType = React.useCallback(
    (type: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, calendarTypes: type ? [type] : [] }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setQpv = React.useCallback(
    (val: boolean | null) => {
      dt.setFilters((prev) => ({ ...prev, isQpv: val ?? undefined }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setSearchQuery = React.useCallback(
    (query: string) => {
      dt.setFilters((prev) => ({ ...prev, searchQuery: query }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setQuotaRange = React.useCallback(
    (min: number | undefined, max: number | undefined) => {
      dt.setFilters((prev) => ({ ...prev, quotaMin: min, quotaMax: max }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setCustomDateRange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dt.setFilters((prev) => ({ ...prev, createdFrom: from, createdTo: to }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setEstablishment = React.useCallback(
    (id: string | undefined, name?: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        selectedEstablishmentId: id,
        searchQuery: id && name ? name : '',
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setProspectionValidated = React.useCallback(
    (val: boolean | null) => {
      dt.setFilters((prev) => ({ ...prev, isProspectionValidated: val }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setSpecialties = React.useCallback(
    (ids: string[]) => {
      dt.setFilters((prev) => ({ ...prev, specialtyIds: ids }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setHasDevoirsFaits = React.useCallback(
    (val: boolean | null) => {
      dt.setFilters((prev) => ({ ...prev, hasDevoirsFaits: val ?? undefined }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setHasInternat = React.useCallback(
    (val: boolean | null) => {
      dt.setFilters((prev) => ({ ...prev, hasInternat: val ?? undefined }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setRenewalNextYear = React.useCallback(
    (val: boolean | null) => {
      dt.setFilters((prev) => ({ ...prev, renewalNextYear: val ?? undefined }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setConventionSigned = React.useCallback(
    (val: boolean | null) => {
      dt.setFilters((prev) => ({ ...prev, conventionSigned: val ?? undefined }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setPartnershipDateRange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dt.setFilters((prev) => ({
        ...prev,
        partnershipDateFrom: from,
        partnershipDateTo: to,
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const filtersContent = React.useMemo(
    () => (
      <EstablishmentFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onToggleType={toggleTypeFilter}
        onToggleCategory={toggleCategoryFilter}
        onTogglePriorityNetwork={togglePriorityNetworkFilter}
        onToggleQpv={setQpv}
        onToggleStatus={toggleStatusFilter}
        onToggleProspectionStatus={toggleProspectionStatusFilter}
        onSetCity={setCity}
        onSetDepartment={setDepartment}
        onSetCalendarType={setCalendarType}
        onSetSearchQuery={setSearchQuery}
        onSetQuotaRange={setQuotaRange}
        onSetEstablishment={setEstablishment}
        onSetCustomDateRange={setCustomDateRange}
        onSetProspectionValidated={setProspectionValidated}
        onToggleHasDevoirsFaits={setHasDevoirsFaits}
        onToggleHasInternat={setHasInternat}
        onToggleRenewalNextYear={setRenewalNextYear}
        onToggleConventionSigned={setConventionSigned}
        onSetPartnershipDateRange={setPartnershipDateRange}
      />
    ),
    [
      dt.filters,
      dt.allowedFilterIds,
      dt.visibleFilterIds,
      toggleTypeFilter,
      toggleCategoryFilter,
      togglePriorityNetworkFilter,
      setQpv,
      toggleStatusFilter,
      toggleProspectionStatusFilter,
      setCity,
      setDepartment,
      setCalendarType,
      setSearchQuery,
      setQuotaRange,
      setEstablishment,
      setCustomDateRange,
      setProspectionValidated,
      setSpecialties,
      setHasDevoirsFaits,
      setHasInternat,
      setRenewalNextYear,
      setConventionSigned,
      setPartnershipDateRange,
    ],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters' as const,
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  const expandedEstablishment = expandedEstablishmentData ?? null;

  return (
    <DataTable
      data={establishments}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="établissements"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={17}
      toolbarActions={
        <>
          {enableFilters && (
            <Button
              preset="toolbar-filter"
              className={cn(dt.showFilters && 'section-action-button-active')}
              showBadge={hasActiveFilters}
              onClick={() => {
                if (dt.showFilters) dt.setShowConfigure(false);
                dt.setShowFilters((prev) => !prev);
              }}
            />
          )}
          {showExport && (
            <Button
              preset="toolbar-export"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isEmptyForExport}
              tooltipText={
                isEmptyForExport && !isExporting
                  ? getErrorMessage('exportEmpty')
                  : 'Exporter la liste des établissements en Excel'
              }
            />
          )}
          {showCreateButton && onCreateClick && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer un établissement"
              onClick={() => {
                onCloseDetails?.();
                onCreateClick();
              }}
            />
          )}
        </>
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={dt.clearFilters}
            resultCount={totalItems}
            resultLabel="établissement(s)"
            onToggleConfigure={
              dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
            }
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ALL_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map((id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn
                          ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                          : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                      )}
                    >
                      {FILTER_GROUP_LABELS[id]}
                    </Button>
                  );
                })}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          <th>Nom</th>
          <th>Type</th>
          <th>Catégorie</th>
          <th>REP</th>
          <th>QPV</th>
          <th>IPS</th>
          <th>Ville</th>
          <th>Département</th>
          <th>Ancienneté partenariat</th>
          <th className="text-center">Salles</th>
          <th className="text-center">
            <EstablishmentTrackingHeader
              label={ESTABLISHMENT_SUIVI_COLUMNS.students.label}
              tooltip={ESTABLISHMENT_SUIVI_COLUMNS.students.tooltip}
            />
          </th>
          <th className="text-center">
            <EstablishmentTrackingHeader
              label={ESTABLISHMENT_SUIVI_COLUMNS.tutors.label}
              tooltip={ESTABLISHMENT_SUIVI_COLUMNS.tutors.tooltip}
            />
          </th>
          <th className="text-center">
            <EstablishmentTrackingHeader
              label={ESTABLISHMENT_SUIVI_COLUMNS.vsc.label}
              tooltip={ESTABLISHMENT_SUIVI_COLUMNS.vsc.tooltip}
            />
          </th>
          <th>Statut de prospection</th>
          <th>Géré par</th>
          <th>
            <EstablishmentTrackingHeader
              label={ESTABLISHMENT_OPENING_DELAY_COLUMN.label}
              tooltip={ESTABLISHMENT_OPENING_DELAY_COLUMN.tooltip}
            />
          </th>
          <th className="text-center">Rendez-vous prospects</th>
          <th>Statut</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(establishment) => (
        <EstablishmentRow
          establishment={establishment}
          onView={onToggleRow ? () => onToggleRow(establishment.id) : undefined}
          onEdit={onEditEstablishment}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(establishment) => (
        <DetailsViewContent>{renderEstablishmentDetails(establishment)}</DetailsViewContent>
      )}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      renderCard={(establishment) => (
        <EstablishmentCard
          establishment={establishment}
          onView={onToggleRow ? () => onToggleRow(establishment.id) : undefined}
          onEdit={onEditEstablishment}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      emptyIcon={Building2}
      emptyText="Aucun établissement trouvé"
      title="Établissements"
      sidebarMenuId="menu-establishments"
      isSection={isSection}
      expandedItem={expandedEstablishment}
      isExpandedDetailLoading={isLoadingExpandedEstablishment}
      onCloseDetails={onCloseDetails}
      renderDetailsContent={(establishment) => (
        <DetailsViewContent>{renderEstablishmentDetails(establishment)}</DetailsViewContent>
      )}
      modals={
        <DeleteModal
          open={!!dt.deleteId}
          onClose={() => dt.setDeleteId(null)}
          onConfirm={() => {
            if (dt.deleteId) deleteMutation.mutate(dt.deleteId);
          }}
          itemName={establishmentToDelete?.name}
          isLoading={deleteMutation.isPending}
        />
      }
    />
  );
};
